// react
import { FormEvent, FormEventHandler, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Box, Grid, InputAdornment } from '@mui/material';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';

// icons
import { ClearOutlined as IconClear, SearchOutlined as IconSearch } from '@mui/icons-material';

// fields
import { Input, InputProps } from '../../fields/Input/Input';

// props
export interface FormSearchProps {
	id?: string;
	handleSearch?: FormEventHandler<HTMLFormElement>;
	hasButton?: boolean;
	InputProps?: InputProps;
	LoadingButtonProps?: LoadingButtonProps;
	paramsPreserved?: string[];
}

export const FormSearch = ({
	handleSearch,
	hasButton = false,
	id = '',
	InputProps,
	LoadingButtonProps,
	paramsPreserved = [],
}: FormSearchProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	const paramQName = InputProps?.name || 'q';

	// params
	const paramQ = searchParams.get(paramQName) || '';

	// state
	const [value, setValue] = useState(paramQ);

	// handlers
	const handleSearchDefault = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (value) {
			// create new search params
			const searchParamsNew = new URLSearchParams();

			// preserve specified params
			searchParams.forEach((paramValue, paramKey) => {
				if (paramsPreserved.includes(paramKey)) {
					searchParamsNew.set(paramKey, paramValue);
				}
			});

			// set search query
			searchParamsNew.set(paramQName, String(value));
			setSearchParams(searchParamsNew);
		} else {
			// remove q search param
			searchParams.delete(paramQName);
			setSearchParams(searchParams);
		}
	};

	const handleClear = () => {
		// remove q param
		searchParams.delete(paramQName);
		setSearchParams(searchParams);

		// clear input
		setValue('');
	};

	return (
		<form id={id || `form-search`} onSubmit={handleSearch || handleSearchDefault}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={true}>
					<Input
						aria-label={t('search')}
						defaultValue={searchParams.get('q')}
						fullWidth
						id="form-search-q"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<Box
										onClick={handleClear}
										sx={(theme) => {
											return {
												marginRight: theme.spacing(-0.5),
												cursor: 'pointer',
											};
										}}>
										<IconClear
											sx={(theme) => {
												return {
													color: theme.palette.brand.grey[500],
													width: theme.spacing(2),
												};
											}}
										/>
									</Box>
								</InputAdornment>
							),
							startAdornment: (
								<InputAdornment position="start">
									<IconSearch />
								</InputAdornment>
							),
						}}
						name="q"
						onChange={(e) => {
							setValue(e.target.value);
						}}
						value={value}
						{...InputProps}
					/>
				</Grid>
				{(hasButton || LoadingButtonProps) && (
					<Grid item xs={12} md="auto">
						<LoadingButton fullWidth type="submit" variant="contained" {...LoadingButtonProps}>
							{t('search')}
						</LoadingButton>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
